import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { usersService, studentCoursesService } from '../../services/firestoreService';
import './StudentsManagement.css';

const StudentsManagement = () => {
  const { user } = useUser();
  const { courses } = useData();
  const [students, setStudents] = useState([]);
  const [studentCoursesMap, setStudentCoursesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignCourses, setShowAssignCourses] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    console.log('📚 StudentsManagement: Setting up subscription...');
    
    const unsubscribe = usersService.subscribeToAllUsers(
      (usersData) => {
        console.log('📚 Received users data:', usersData.length, 'users');
        // Filter to show only students (not coaches or admins)
        const studentsOnly = usersData.filter(u => u.type === 'student');
        console.log('👥 Filtered to students:', studentsOnly.length);
        setStudents(studentsOnly);
        
        // Load courses for each student
        if (studentsOnly.length > 0) {
          Promise.all(
            studentsOnly.map(async (student) => {
              try {
                const studentCourses = await studentCoursesService.getStudentCourses(student.id);
                return { studentId: student.id, courses: studentCourses.map(sc => sc.courseId) };
              } catch (error) {
                console.error(`Error loading courses for student ${student.id}:`, error);
                return { studentId: student.id, courses: [] };
              }
            })
          ).then(results => {
            const map = {};
            results.forEach(({ studentId, courses }) => {
              map[studentId] = courses;
            });
            setStudentCoursesMap(map);
            setLoading(false);
            console.log('✅ Students loaded successfully');
          }).catch(error => {
            console.error('Error loading student courses:', error);
            setError('Error loading student courses');
            setLoading(false);
          });
        } else {
          setLoading(false);
          console.log('ℹ️ No students found');
        }
      },
      (error) => {
        console.error('❌ Error in users subscription:', error);
        setError('Error loading students. Please refresh the page.');
        setLoading(false);
      }
    );

    return () => {
      console.log('📚 StudentsManagement: Cleaning up subscription...');
      unsubscribe();
    };
  }, []);

  const toggleStudentStatus = async (id) => {
    try {
      const student = students.find(s => s.id === id);
      const newActiveStatus = student.active === false ? true : false;
      await usersService.updateUser(id, { active: newActiveStatus });
    } catch (error) {
      console.error('Error updating student status:', error);
      alert('Error updating student status. Please try again.');
    }
  };

  const handleAssignCourses = async (studentId, selectedCourses) => {
    try {
      await studentCoursesService.updateStudentCourses(studentId, selectedCourses, user.id);
      setStudentCoursesMap({
        ...studentCoursesMap,
        [studentId]: selectedCourses
      });
      setShowAssignCourses(null);
    } catch (error) {
      console.error('Error assigning courses:', error);
      alert('Error assigning courses. Please try again.');
    }
  };

  const handleAssignCoachRole = async (userId) => {
    if (window.confirm('Assign coach role to this user?')) {
      try {
        await usersService.updateUser(userId, { type: 'coach' });
        alert('User assigned as coach!');
      } catch (error) {
        console.error('Error assigning coach role:', error);
        alert('Error assigning coach role. Please try again.');
      }
    }
  };

  const handleRemoveCoachRole = async (userId) => {
    if (window.confirm('Remove coach role from this user? They will become a student.')) {
      try {
        await usersService.updateUser(userId, { type: 'student' });
        alert('Coach role removed!');
      } catch (error) {
        console.error('Error removing coach role:', error);
        alert('Error removing coach role. Please try again.');
      }
    }
  };

  const handleEditEmail = (student) => {
    setEditingEmail(student.id);
    setNewEmail(student.email || '');
  };

  const handleSaveEmail = async (studentId) => {
    if (!newEmail || !newEmail.trim()) {
      alert('Please enter a valid email address.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      await usersService.updateUser(studentId, { email: newEmail.trim().toLowerCase() });
      alert('✅ Email updated successfully!');
      setEditingEmail(null);
      setNewEmail('');
    } catch (error) {
      console.error('Error updating email:', error);
      alert('Error updating email. Please try again.');
    }
  };

  const handleCancelEditEmail = () => {
    setEditingEmail(null);
    setNewEmail('');
  };

  return (
    <div className="students-management">
      <div className="page-header">
        <h1>👥 Students Management</h1>
      </div>

      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          marginBottom: '20px',
          color: '#991b1b'
        }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      <div className="students-list">
        {loading ? (
          <div className="empty-state">
            <p>Loading students...</p>
          </div>
        ) : error ? (
          <div className="empty-state">
            <p>❌ {error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              🔄 Refresh Page
            </button>
          </div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <p>No students found.</p>
            <p style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '8px' }}>
              Students will appear here once they register for an account.
            </p>
          </div>
        ) : (
          students.map((student) => {
            const assignedCourses = studentCoursesMap[student.id] || [];
            return (
            <div key={student.id} className={`student-card ${student.active === false ? 'inactive' : ''}`}>
              <div className="student-header">
                <h3>{student.name || student.email?.split('@')[0] || 'Unnamed Student'}</h3>
                <span className={`status-badge ${student.active !== false ? 'active' : 'inactive'}`}>
                  {student.active !== false ? '✅ Active' : '❌ Inactive'}
                </span>
              </div>
              <div className="student-email-section">
                {editingEmail === student.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email"
                      style={{
                        flex: 1,
                        minWidth: '250px',
                        padding: '10px 14px',
                        border: '2px solid #6366f1',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'white'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#6366f1'}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEmail(student.id);
                        }
                      }}
                    />
                    <button
                      onClick={() => handleSaveEmail(student.id)}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={handleCancelEditEmail}
                      style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        boxShadow: '0 2px 4px rgba(107, 114, 128, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(107, 114, 128, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(107, 114, 128, 0.3)';
                      }}
                    >
                      ✕ Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <p className="student-email" style={{ margin: 0, flex: 1, minWidth: '200px' }}>{student.email}</p>
                    <button
                      onClick={() => handleEditEmail(student)}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(99, 102, 241, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.3)';
                      }}
                      title="Edit email"
                    >
                      ✏️ Edit Email
                    </button>
                  </div>
                )}
              </div>
              
              <div className="student-courses">
                <strong>Assigned Courses:</strong>
                {assignedCourses.length === 0 ? (
                  <span className="no-courses">No courses assigned</span>
                ) : (
                  <div className="courses-badges">
                    {assignedCourses.map(courseId => {
                      const course = courses.find(c => c.id === courseId);
                      return course ? (
                        <span key={courseId} className="course-badge">{course.title}</span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              <div className="student-info">
                <div className="user-type-badge">
                  <strong>Type:</strong> {student.type === 'coach' ? '👨‍🏫 Coach' : student.type === 'admin' ? '👑 Admin' : '👤 Student'}
                </div>
              </div>
              <div className="student-actions">
                <button 
                  onClick={() => toggleStudentStatus(student.id)}
                  className={`status-btn ${student.active !== false ? 'deactivate-btn' : 'activate-btn'}`}
                >
                  {student.active !== false ? '❌ Deactivate' : '✅ Activate'}
                </button>
                {student.type === 'student' && (
                  <button 
                    onClick={() => setShowAssignCourses(student.id)}
                    className="assign-btn"
                  >
                    📚 Assign Courses
                  </button>
                )}
                {student.type !== 'coach' && student.type !== 'admin' && (
                  <button 
                    onClick={() => handleAssignCoachRole(student.id)}
                    className="coach-btn"
                  >
                    👨‍🏫 Make Coach
                  </button>
                )}
                {student.type === 'coach' && (
                  <button 
                    onClick={() => handleRemoveCoachRole(student.id)}
                    className="remove-coach-btn"
                  >
                    ❌ Remove Coach Role
                  </button>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>

      {showAssignCourses && (
        <div className="modal-overlay" onClick={() => setShowAssignCourses(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Assign Courses</h2>
            <CourseAssignmentForm
              student={students.find(s => s.id === showAssignCourses)}
              courses={courses}
              currentCourses={studentCoursesMap[showAssignCourses] || []}
              onAssign={handleAssignCourses}
              onCancel={() => setShowAssignCourses(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CourseAssignmentForm = ({ student, courses, onAssign, onCancel, currentCourses = [] }) => {
  const [selectedCourses, setSelectedCourses] = useState(currentCourses);

  const toggleCourse = (courseId) => {
    if (selectedCourses.includes(courseId)) {
      setSelectedCourses(selectedCourses.filter(id => id !== courseId));
    } else {
      setSelectedCourses([...selectedCourses, courseId]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAssign(student.id, selectedCourses);
  };

  return (
    <form onSubmit={handleSubmit}>
      <p>Select courses for <strong>{student.name}</strong>:</p>
      <div className="courses-selection">
        {courses.length === 0 ? (
          <p className="no-courses-msg">No courses available. Create courses first.</p>
        ) : (
          courses.map(course => (
            <label key={course.id} className="course-checkbox">
              <input
                type="checkbox"
                checked={selectedCourses.includes(course.id)}
                onChange={() => toggleCourse(course.id)}
              />
              <span>{course.title}</span>
            </label>
          ))
        )}
      </div>
      <div className="form-actions">
        <button type="submit" className="save-btn">💾 Save</button>
        <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
      </div>
    </form>
  );
};

export default StudentsManagement;

